import { Action, State, StateContext, Store } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { catchError, filter, finalize, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { EntitiesRepository, LCLBillsRepository, PackagesRepository, QuoteRepository, ShipmentRepository } from '@data-layer/repositories';
import { LCLShippingStateModel } from './lcl-shipping-state.model';
import {
  CloseLCLMarineInsuranceQuoteSidebar,
  CreateExistingUserManualQuote,
  CreateExistingUserManualQuoteError,
  CreateExternalManualQuote,
  CreateExternalManualQuoteError,
  CreateExternalManualQuoteSuccess,
  CreateLCLLinkToES,
  CreateLCLLinkToESSuccess,
  CreateLCLLinkToISF,
  CreateLCLLinkToISFSuccess,
  CreateManualQuote,
  CreateManualQuoteSuccess,
  CreateQuote,
  CreateQuoteSuccess,
  FetchLCLManualQuotes,
  FetchLCLMarineInsuranceDocuments,
  FetchLCLMarineInsurancePendingBills,
  FetchLCLPendingBills,
  FetchLCLQuotes,
  FetchLCLShipments,
  FetchQuote,
  FetchQuoteError,
  FetchQuoteSuccess,
  FetchShipment,
  FetchShipmentError,
  FetchShipmentSuccess,
  GetLCLMarineInsurance,
  OffsetChange,
  OpenLCLMarineInsuranceQuoteSidebar,
  OrderQuote,
  OrderQuoteSuccess,
  PatchShipmentEntity,
  RemoveLCLMarineInsurance,
  ResetLCLData,
  ResetLCLPendingBills,
  SetDefaultPaginationState,
  SubmitExitIntent,
  SubmitExitIntentError,
  SubmitExitIntentSuccess,
  UpdateLCLEntity,
  UpdateLoadingState,
  UpdateQuote,
  UpdateQuoteDraft,
  UpdateQuoteIncoterms,
  UpdateQuoteSuccess,
  UpdateQuoteVoyage,
  UpdateShipment,
  UpdateShipmentSuccess,
} from './lcl-shipping.actions';
import { Injectable } from '@angular/core';
import { LCLShipmentModel, QuoteManualDetailedModel, QuoteManualModel, QuoteModel, SurveyInfoModel } from '@data-layer/models/lcl';
import { PaginatedResultModel } from '@data-layer/models/common';
import { LclQuoteCardModel } from '@data-layer/models/lcl/lcl-quote-card.model';
import { LclShipmentCardModel } from '@data-layer/models/lcl/lcl-shipment-card.model';
import { IsfStateService } from '@shared/isf-mutual/services';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { IsfModel } from '@data-layer/models/isf';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { HttpErrorResponse } from '@angular/common/http';
import { enumIsfShipmentCode } from '@data-layer/enums/isf';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { LclQuoteService } from '@app/modules/lcl-shipping/services';
import { CompaniesService } from '@services/companies.service';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import {
  MarineInsuranceAvailabilityService,
  MarineInsuranceFacade,
  MarineInsuranceSideformService,
  MarineInsuranceState,
} from '@shared/marine-insurance-shared/services';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { AppService } from '@app/app.service';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { DocumentModel } from '@data-layer/models/documents';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { isDefined } from '@services/utils/utils.service';
import { AuthService } from '@app/modules/auth/services';
import { CompanyModel } from '@data-layer/models/companies';

const defaults: LCLShippingStateModel = {
  isLoading: true,
  offset: 0,
  limit: 20,
  total: 0,
  quotes: [],
  shipments: [],
  selectedQuote: null,
  selectedQuoteLCLPayments: null,
  manualQuotes: [],
  selectedShipment: null,
};

@State<LCLShippingStateModel>({
  name: 'lclShipping',
  defaults,
})
@Injectable()
export class LCLShippingState {
  constructor(
    private quoteRepository: QuoteRepository,
    private shipmentRepository: ShipmentRepository,
    private packagesRepository: PackagesRepository,
    private entitiesRepository: EntitiesRepository,
    private billsRepository: LCLBillsRepository,
    private store: Store,
    private isfStateService: IsfStateService,
    private esStateService: EntrySummaryStateService,
    private ga: GoogleAnalyticsService,
    private lclQuoteService: LclQuoteService,
    private companiesService: CompaniesService,
    private marineInsuranceState: MarineInsuranceState,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
    private marineInsuranceSideformService: MarineInsuranceSideformService,
    private marineInsuranceFacade: MarineInsuranceFacade,
    private appService: AppService,
    private authService: AuthService,
  ) {}

  @Action(UpdateLoadingState)
  public updateLoadingState(ctx: StateContext<LCLShippingStateModel>, { payload }: UpdateLoadingState) {
    return ctx.patchState({ isLoading: payload });
  }

  @Action(OffsetChange)
  public offsetChange(ctx: StateContext<LCLShippingStateModel>, { payload }: OffsetChange) {
    return ctx.patchState({ offset: payload });
  }

  @Action(ResetLCLData)
  public resetLCLData(ctx: StateContext<LCLShippingStateModel>): void {
    ctx.patchState({ ...defaults });
  }

  @Action(FetchLCLQuotes, { cancelUncompleted: true })
  public fetchLCLQuotes(ctx: StateContext<LCLShippingStateModel>, { payload }: FetchLCLQuotes) {
    return this.quoteRepository.search(payload.companyId, { limit: payload.limit, offset: payload.offset }).pipe(
      tap((response: PaginatedResultModel<LclQuoteCardModel>) =>
        ctx.setState(
          patch({
            quotes: append([...response.items]),
            total: response.total,
          }),
        ),
      ),
    );
  }

  @Action(FetchLCLManualQuotes, { cancelUncompleted: true })
  public fetchLCLManualQuotes(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: FetchLCLManualQuotes,
  ): Observable<PaginatedResultModel<QuoteManualDetailedModel>> {
    return this.quoteRepository.searchManual(payload.companyId, { limit: payload.limit, offset: payload.offset }).pipe(
      tap((response: PaginatedResultModel<QuoteManualDetailedModel>) =>
        ctx.setState(
          patch({
            manualQuotes: append([...response.items]),
          }),
        ),
      ),
    );
  }

  @Action(FetchLCLShipments, { cancelUncompleted: true })
  public fetchLCLShipments(ctx: StateContext<LCLShippingStateModel>, { payload }: FetchLCLShipments) {
    return this.shipmentRepository.getAll(payload.companyId, { limit: payload.limit, offset: payload.offset }).pipe(
      tap((response: PaginatedResultModel<LclShipmentCardModel>) =>
        ctx.setState(
          patch({
            shipments: [...response.items],
            // total: response.total,
          }),
        ),
      ),
    );
  }

  @Action(FetchQuote)
  public fetchQuote(ctx: StateContext<LCLShippingStateModel>, { payload }: FetchQuote): Observable<QuoteModel> {
    const isLoading: boolean = isDefined(payload.isLoading) ? payload.isLoading : true;

    return this.quoteRepository.get(payload.quoteId, isLoading).pipe(
      tap((response: QuoteModel) => {
        ctx.patchState({ selectedQuote: response, isLoading: false });
        this.store.dispatch(new FetchQuoteSuccess());
      }),
      catchError((err: HttpErrorResponse) => this.store.dispatch(new FetchQuoteError(err))),
    );
  }

  @Action(FetchLCLPendingBills)
  public fetchLCLBills(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: FetchLCLPendingBills,
  ): Observable<PaymentBillItemPendingModel> {
    // ctx.patchState({ selectedQuotePayments: null });

    return this.billsRepository
      .pendingBills(payload.companyId, [payload.referenceNumber], null, null, payload.coupon, false, false)
      .pipe(tap((response: PaymentBillItemPendingModel) => ctx.patchState({ selectedQuoteLCLPayments: response })));
  }

  @Action(ResetLCLPendingBills)
  public cleanLCLBills(ctx: StateContext<LCLShippingStateModel>): void {
    ctx.patchState({ selectedQuote: null, selectedQuoteLCLPayments: null });
  }

  @Action(FetchLCLMarineInsurancePendingBills)
  public fetchLCLMarineInsuranceBills(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: FetchLCLPendingBills,
  ): Observable<PaymentBillItemPendingModel> {
    return this.marineInsuranceFacade
      .getPendingBills(payload.companyId, [payload.referenceNumber], null, null, false, payload?.coupon)
      .pipe(tap((response: PaymentBillItemPendingModel) => this.marineInsuranceState.saveBillsData(response)));
  }

  @Action(FetchShipment)
  public fetchShipment(ctx: StateContext<LCLShippingStateModel>, { payload }: FetchShipment): Observable<LCLShippingStateModel> {
    ctx.patchState({ isLoading: true });

    return this.shipmentRepository.get(payload).pipe(
      tap((response: LCLShipmentModel) => {
        ctx.patchState({ selectedShipment: response, isLoading: false });
        this.store.dispatch(new FetchShipmentSuccess());
      }),
      catchError((err: HttpErrorResponse) => this.store.dispatch(new FetchShipmentError(err))),
    );
  }

  @Action(UpdateLCLEntity)
  public updateImporter(ctx: StateContext<LCLShippingStateModel>, { payload }: UpdateLCLEntity) {
    return this.entitiesRepository.updateEntity(payload.entityId, payload.entity);
  }

  @Action(SetDefaultPaginationState)
  public setDefaultPaginationState(ctx: StateContext<LCLShippingStateModel>) {
    const { offset, limit, total, quotes } = defaults;

    return ctx.patchState({ offset, limit, total, quotes: quotes });
  }

  @Action(CreateQuote)
  public createQuote(ctx: StateContext<LCLShippingStateModel>, { payload }: CreateQuote): Observable<QuoteModel> {
    ctx.patchState({ isLoading: true });
    // create shipper -> create quote

    return (
      this.companiesService.validateCompany(enumCompanyPlacement.SHIPMENT)
        ? this.entitiesRepository.createEntity(payload.companyId, !!payload.shipper ? payload.shipper : payload.consignee)
        : of(null)
    ).pipe(
      switchMap((entity: EntityModel) =>
        this.quoteRepository.create({
          ...payload.quote,
          ...(entity?.entityCode === enumEntityModelCode.CONSIGNEE && { consigneeId: entity.id }),
          ...(entity?.entityCode === enumEntityModelCode.SHIPPER && { shipperId: entity.id }),
        }),
      ),
      tap((quote: QuoteModel) => {
        this.ga.emitCustomEvent(enumGATagEventCategory.EezyFreight, enumGATagEventAction.EezyFreightGetQuote, `${quote.referenceNumber}`);
        this.store.dispatch(new CreateQuoteSuccess(quote.id));
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(UpdateQuote)
  public updateQuote(ctx: StateContext<LCLShippingStateModel>, { payload }: UpdateQuote) {
    const isLoading: boolean = isDefined(payload.isLoading) ? payload.isLoading : true;
    ctx.patchState({ isLoading });

    return this.quoteRepository.update(payload?.quote, isLoading).pipe(
      tap((quote: QuoteModel) => {
        this.store.dispatch(new UpdateQuoteSuccess(quote.id));
        ctx.patchState({ isLoading: false });
      }),
    );
  }

  @Action(UpdateQuoteIncoterms)
  public updateQuoteIncoterms(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: UpdateQuoteIncoterms,
  ): Observable<[QuoteModel, EntityModel[]]> {
    ctx.patchState({ isLoading: true });

    return this.quoteRepository.update(payload).pipe(
      mergeMap((quote: QuoteModel) => combineLatest([of(quote), this.lclQuoteService.cleanOldEntities()])),
      tap(([quote]: [QuoteModel, EntityModel[]]) => {
        ctx.patchState({ isLoading: false });
        this.store.dispatch(new UpdateQuoteSuccess(payload.id));
      }),
    );
  }

  @Action(UpdateQuoteDraft)
  public updateQuoteDraft(ctx: StateContext<LCLShippingStateModel>, { payload }: UpdateQuoteDraft): void {
    ctx.patchState({ selectedQuote: payload });
  }

  @Action(UpdateQuoteVoyage)
  public updateQuoteVoyage(ctx: StateContext<LCLShippingStateModel>, { payload }: UpdateQuoteVoyage) {
    ctx.patchState({ isLoading: true });

    return this.quoteRepository.updateVoyage(payload.quoteId, payload.voyageId).pipe(
      tap(() => {
        this.store.dispatch(new FetchQuote({ quoteId: payload.quoteId }));
      }),
    );
  }

  @Action(CreateManualQuote)
  public createManualQuote(ctx: StateContext<LCLShippingStateModel>, { payload }: CreateManualQuote): Observable<QuoteManualModel> {
    ctx.patchState({ isLoading: true });

    return this.quoteRepository.createManual(payload.companyId, payload.quote).pipe(
      tap((quote: QuoteManualModel) => {
        this.ga.emitCustomEvent(enumGATagEventCategory.EezyFreight, enumGATagEventAction.EezyFreightGetManualQuote, `${quote}`);
        this.store.dispatch(new CreateManualQuoteSuccess());
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }

  @Action(CreateExternalManualQuote)
  public createExternalManualQuote(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: CreateExternalManualQuote,
  ): Observable<QuoteManualModel> {
    ctx.patchState({ isLoading: true });

    return this.quoteRepository.createExternalManual(payload.quote, payload.recaptchaToken, false).pipe(
      tap((quote: QuoteManualModel) => {
        this.ga.emitCustomEvent(enumGATagEventCategory.EezyFreight, enumGATagEventAction.EezyFreightGetManualQuote, `${quote}`);
        this.store.dispatch(new CreateExternalManualQuoteSuccess());
      }),
      finalize(() => ctx.patchState({ isLoading: false })),
      catchError((e) => {
        if (e instanceof HttpErrorResponse && [400, 403].includes(e.status)) {
          console.log(e);
          const { tag } = e.error;
          this.store.dispatch(new CreateExternalManualQuoteError({ tag }));

          return EMPTY;
        }
        return EMPTY;
      }),
    );
  }

  @Action(CreateExistingUserManualQuote)
  public createExistingUserManualQuote(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: CreateExistingUserManualQuote,
  ): Observable<CompanyModel> {
    this.authService.resetLoginData();

    return this.authService.loginAction(payload.email, payload.password, payload.formGroup, true).pipe(
      tap(() => {
        this.store.dispatch(new CreateManualQuote({ companyId: this.companiesService.getCompany()?.id, quote: payload?.quote }));
      }),
      catchError((e) => {
        if (!!payload?.formGroup?.errors?.resError?.code) {
          this.store.dispatch(new CreateExistingUserManualQuoteError({ tag: payload?.formGroup?.errors?.resError?.code }));
        }

        if (e instanceof HttpErrorResponse && [400, 403].includes(e.status)) {
          console.log(e);
          const { tag } = e.error;
          if (!!tag) {
            this.store.dispatch(new CreateExistingUserManualQuoteError({ tag }));
          }

          return EMPTY;
        }
        return EMPTY;
      }),
    );
  }

  @Action(OrderQuote)
  public orderQuote(ctx: StateContext<LCLShippingStateModel>, { payload }: OrderQuote) {
    ctx.patchState({ isLoading: true });
    // create entity -> update existing quote consignee/shipper -> create shipment

    return this.shipmentRepository
      .create({
        quoteId: payload.quote.id,
      })
      .pipe(
        tap((shipment: LCLShipmentModel) => {
          this.ga.emitCustomEvent(
            enumGATagEventCategory.EezyFreight,
            enumGATagEventAction.EezyFreightPlaceOrder,
            `${payload.quote.referenceNumber}_${shipment.referenceNumber}`,
          );
          this.store.dispatch(new OrderQuoteSuccess(shipment.id));
        }),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
  }

  @Action(UpdateShipment)
  public updateShipment(ctx: StateContext<LCLShippingStateModel>, { payload }: UpdateShipment): Observable<LCLShipmentModel> {
    ctx.patchState({ isLoading: true });

    const normilizedShipment: LCLShipmentModel = {
      ...payload,
      consigneeId: payload?.consigneeId || payload?.consignee?.id,
      notifyPartyId: payload?.notifyPartyId || payload?.notifyParty?.id,
      shipperId: payload?.shipperId || payload?.shipper?.id,
    };

    return this.shipmentRepository.update(normilizedShipment).pipe(
      tap((shipment: LCLShipmentModel) => {
        this.store.dispatch(new UpdateShipmentSuccess());
        ctx.patchState({ selectedShipment: shipment, isLoading: false });
      }),
    );
  }

  @Action(PatchShipmentEntity)
  public patchShipmentEntity(ctx: StateContext<LCLShippingStateModel>, { payload }: PatchShipmentEntity) {
    ctx.patchState({ selectedShipment: { ...ctx.getState().selectedShipment, [payload.fieldKey]: payload.entity } });
  }

  @Action(CreateLCLLinkToISF)
  public createLCLLinkToISF(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: CreateLCLLinkToISF,
  ): Observable<LCLShipmentModel | IsfModel> {
    ctx.patchState({ isLoading: true });

    if (payload.data.isInternalFillingSelected) {
      return this.isfStateService
        .createEmptyISF(
          {
            importer: this.isfStateService.initImporterFromCompany(0),
            seller: { ...payload.shipment.shipper, entityIdentifier: null, entityIdentifierQualifier: null },
            consignee: { ...payload.shipment.consignee, entityIdentifier: null, entityIdentifierQualifier: null },
            houseBill: {
              carrier: payload.shipment.houseBill?.carrier,
              number: payload.shipment.houseBill?.number,
            },
            code: enumIsfShipmentCode.HOUSE,
          },
          payload.shipment.id,
          payload.data,
        )
        .pipe(
          tap((isf: IsfModel) => this.store.dispatch(new CreateLCLLinkToISFSuccess(isf?.referenceNumber))),
          finalize(() => ctx.patchState({ isLoading: false })),
        );
    } else {
      return this.shipmentRepository
        .setISFData(payload.shipment.id, { isInternalFillingSelected: false, externalCbpNumber: payload.data.externalCbpNumber })
        .pipe(
          tap(() => this.store.dispatch(new CreateLCLLinkToISFSuccess())),
          finalize(() => ctx.patchState({ isLoading: false })),
        );
    }
  }

  @Action(CreateLCLLinkToES)
  public createLCLLinkToES(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: CreateLCLLinkToES,
  ): Observable<EntrySummaryModel | LCLShipmentModel> {
    ctx.patchState({ isLoading: true });

    if (payload.data.isInternalFillingSelected) {
      const _shipment: LCLShipmentModel = {
        ...payload.shipment,
        incoTermsType: payload.shipment.incoTermsType === enumIncotermsType.CPT_CFR ? null : payload.shipment.incoTermsType,
        shipper: { ...payload.shipment.shipper, entityIdentifier: null, entityIdentifierQualifier: null },
      };
      return this.esStateService
        .createEmptyES(_shipment, payload.freightCost, payload.shipment.id, payload.data, payload.invoiceDocument)
        .pipe(
          tap((entrySummary: EntrySummaryModel) => this.store.dispatch(new CreateLCLLinkToESSuccess(entrySummary?.referenceNumber))),
          finalize(() => ctx.patchState({ isLoading: false })),
        );
    } else {
      return this.shipmentRepository.setESData(payload.shipment.id, { isInternalFillingSelected: false }).pipe(
        tap(() => this.store.dispatch(new CreateLCLLinkToESSuccess())),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
    }
  }

  @Action(GetLCLMarineInsurance)
  public getLCLMarineInsurance(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: GetLCLMarineInsurance,
  ): Observable<{ insurance: MarineInsuranceModel; isAvailable: boolean }> {
    this.marineInsuranceState.resetData();

    return this.marineInsuranceAvailabilityService
      .isShowCreateForShipment({
        freightShipment: payload.freightShipment,
        freightQuote: payload.freightQuote,
      })
      .pipe(
        tap((data: { insurance: MarineInsuranceModel; isAvailable: boolean }) => {
          if (!!data.insurance?.certificate?.issueDate) {
            this.marineInsuranceState.saveData(data.insurance, data.isAvailable, true);
            this.store.dispatch(new FetchLCLMarineInsuranceDocuments(data.insurance?.id));
          } else {
            this.marineInsuranceState.saveData(data.insurance, data.isAvailable, false);
          }
          this.store.dispatch(
            new FetchLCLMarineInsurancePendingBills({
              companyId: !!payload.freightQuote?.referenceNumber
                ? ctx.getState()?.selectedQuote?.companyId
                : ctx.getState()?.selectedShipment?.companyId,
              referenceNumber: data.insurance?.referenceNumber,
            }),
          );
        }),
        catchError(() => {
          this.marineInsuranceState.saveData(null, true, false);
          return EMPTY;
        }),
      );
  }

  @Action(RemoveLCLMarineInsurance)
  public removeLCLMarineInsurance(ctx: StateContext<LCLShippingStateModel>, { shipmentId }: RemoveLCLMarineInsurance): Observable<never> {
    this.marineInsuranceState.resetData();

    return this.marineInsuranceFacade.deleteShipment(shipmentId, false).pipe(tap(() => this.marineInsuranceState.saveBillsData(null)));
  }

  @Action(OpenLCLMarineInsuranceQuoteSidebar)
  public openLCLMarineInsuranceQuoteSidebar(
    ctx: StateContext<LCLShippingStateModel>,
    { payload }: OpenLCLMarineInsuranceQuoteSidebar,
  ): void {
    this.marineInsuranceSideformService.onOpenQuoteSidebar(
      !!payload?.booking ? enumMarineInsuranceEntityType.LCL_QUOTE : enumMarineInsuranceEntityType.LCL_SHIPPING,
      { lclQuote: payload?.booking, lclShipment: payload?.shipment },
      this.marineInsuranceState.currentInsurance.getValue(),
      !!this.marineInsuranceState.currentInsurance.getValue(),
    );

    this.appService.isClickOnCloseSubject
      .pipe(
        filter((val: boolean) => !!val),
        take(1),
      )
      .subscribe(() => {
        this.store.dispatch(new CloseLCLMarineInsuranceQuoteSidebar(true));
      });
  }

  @Action(FetchLCLMarineInsuranceDocuments)
  public fetchLCLMarineInsuranceDocuments(
    ctx: StateContext<LCLShippingStateModel>,
    { shipmentId }: FetchLCLMarineInsuranceDocuments,
  ): Observable<DocumentModel> {
    return this.marineInsuranceFacade.getDocuments(shipmentId).pipe(
      tap((response: DocumentModel) => {
        this.marineInsuranceState.saveDocsData(response);
        this.marineInsuranceState.saveIsLoadingData(false);
      }),
    );
  }

  @Action(SubmitExitIntent)
  public submitExitIntentAnswer(ctx: StateContext<LCLShippingStateModel>, { payload }: SubmitExitIntent): Observable<SurveyInfoModel> {
    ctx.patchState({ isLoading: true });

    return this.quoteRepository.submitExitIntentAnswer(payload, false).pipe(
      tap(() => {
        this.store.dispatch(new SubmitExitIntentSuccess());
      }),
      catchError(() => this.store.dispatch(new SubmitExitIntentError())),
      finalize(() => ctx.patchState({ isLoading: false })),
    );
  }
}
